import React, { useEffect, useState } from "react";
import { useGenerateTotpQrCodeMutation } from "common/types/mainHooks";
import DOMPurify from "dompurify";
import { Flex, OrderedList, OrderedListItem, Text } from "@jsq/ds/core";
import { type UnauthenticatedArenaObjectType } from "../../../types";
import { SetupCard } from "../components/SetupCard";

type Props = {
  onSubmit: (code?: string) => void;
  onSwitch2FAMethodButtonClick?: () => void;
  arena?: UnauthenticatedArenaObjectType;
  isSplitScreen?: boolean;
  emailAddress?: string;
  password?: string;
};

/**
 * A component for setting up TOTP MFA with an authenticator app.
 */
export const TotpSetupVariant = ({
  onSwitch2FAMethodButtonClick,
  arena,
  isSplitScreen,
  onSubmit,
  emailAddress,
  password,
}: Props) => {
  const [totpKey, setTotpKey] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [generateTotp] = useGenerateTotpQrCodeMutation({
    context: { unauthenticated: true },
  });

  const GenerateTotpLink = async () => {
    try {
      const result = await generateTotp({
        variables: {
          objectInput: {
            emailAddress: emailAddress || "",
            password: password || "",
            forceUpdate: true,
          },
        },
      });

      if (result.data?.generateTotpQrCode?.__typename === "GenerateTotpQrCodeOutput") {
        setTotpKey(String(result.data?.generateTotpQrCode?.totpKey || ""));
        setQrCode(String(result.data?.generateTotpQrCode?.qrCode || ""));
        setIsLoading(false);
      }
    } catch {
      // Mutation failure leaves isLoading=true, button stays disabled
    }
  };

  useEffect(() => {
    GenerateTotpLink();
  }, []);

  return (
    <SetupCard
      onSwitch2FAMethodButtonClick={onSwitch2FAMethodButtonClick}
      methodTitle="Add account to authenticator app"
      methodSubtitle="You can use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator."
      submitButtonText="Continue"
      switch2FAMethodButtonText="Try another method to authenticate"
      onSubmit={() => onSubmit(totpKey)}
      // @ts-expect-error code written before better codegen types
      submitButtonBackgroundColor={arena?.signInButtonBackgroundColor}
      // @ts-expect-error code written before better codegen types
      submitButtonTextColor={arena?.signInButtonTextColor}
      isSplitScreen={isSplitScreen}
      isSubmitting={isLoading}
    >
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={12}>
        <OrderedList>
          <OrderedListItem>
            <Text variant="secondary-md">Open your authenticator app.</Text>
          </OrderedListItem>
          <OrderedListItem>
            <Text variant="secondary-md">
              Scan or manually input this code to enroll your Juniper Square account with your
              authenticator app.
            </Text>
          </OrderedListItem>
        </OrderedList>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(qrCode) }} />
        <Text variant="strong-xl">{totpKey}</Text>
      </Flex>
    </SetupCard>
  );
};

export default TotpSetupVariant;
